import { Link } from "../../../../../router/LocaleLink";
import { Trans, useTranslation } from "react-i18next";
import { Paths } from "../../../../../router/paths";
import { trackForBusinessLinkClicked } from "../../../../../analytics/amplitudeTracking";
import {
  APP_STORE_URL,
  HomeStaticAssets,
  PLAY_STORE_URL,
} from "../../figmaAssets";
import styles from "./Landing.module.css";

const FOOTER_NAV: {
  labelKey:
    | "aboutUs"
    | "aboutFounder"
    | "ourMission"
    | "forBusiness"
    | "mcp"
    | "startups"
    | "referral"
    | "stockScreener"
    | "etfScreener"
    | "contactUs"
    | "screeningMethodology"
    | "privacyPolicy"
    | "termsAndConditions"
    | "disclosures";
  href: string;
  external: boolean;
}[] = [
  { labelKey: "aboutUs", href: Paths.ABOUT_US, external: false },
  { labelKey: "aboutFounder", href: Paths.ABOUT_FOUNDER, external: false },
  { labelKey: "ourMission", href: Paths.OUR_MISSION, external: false },
  { labelKey: "forBusiness", href: Paths.FOR_BUSINESS, external: false },
  { labelKey: "mcp", href: Paths.MCP, external: false },
  { labelKey: "startups", href: Paths.STARTUPS, external: false },
  { labelKey: "referral", href: Paths.REFERRAL, external: false },
  { labelKey: "stockScreener", href: Paths.STOCK_SCREENER, external: false },
  { labelKey: "etfScreener", href: Paths.ETF_SCREENER, external: false },
  { labelKey: "contactUs", href: Paths.CONTACT, external: false },
  {
    labelKey: "screeningMethodology",
    href: Paths.SHARIAH_SCREENING_METHODOLOGY,
    external: false,
  },
  { labelKey: "privacyPolicy", href: Paths.PRIVACY_POLICY, external: false },
  {
    labelKey: "termsAndConditions",
    href: Paths.TERMS_AND_CONDITIONS,
    external: false,
  },
  { labelKey: "disclosures", href: Paths.DISCLOSURES, external: false },
];

const SOCIAL_LINKS: {
  href: string;
  icon: string;
  labelKey: "socialLinkedin" | "socialInstagram" | "socialTelegram";
}[] = [
  {
    href: "https://www.linkedin.com/company/akindaio",
    icon: HomeStaticAssets.footerSocial.linkedin,
    labelKey: "socialLinkedin",
  },
  {
    href: "https://www.instagram.com/akinda.io?igsh=MTNzd2ZkbTF5b3E1YQ==",
    icon: HomeStaticAssets.footerSocial.instagram,
    labelKey: "socialInstagram",
  },
  {
    href: "https://t.me/akindaio",
    icon: HomeStaticAssets.footerSocial.telegram,
    labelKey: "socialTelegram",
  },
];

export const LandingFooter = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const s = HomeStaticAssets;

  return (
    <footer className={styles.landingFooter} role="contentinfo">
      <div className={styles.landingFooterInner}>
        <div className={styles.landingFooterTop}>
          <div className={styles.landingFooterBrand}>
            <div className={styles.landingFooterLogoRow}>
              <img
                src={s.footerLogo}
                alt={t("home.footer.logoAlt")}
                width={160}
                height={20}
                className={styles.landingFooterLogo}
              />
            </div>
            <p className={styles.landingFooterTagline}>
              {t("home.footer.tagline")}
            </p>
            <nav
              className={styles.landingFooterNav}
              aria-label={t("home.footer.navAria")}
            >
              {FOOTER_NAV.map((item) => {
                const label = t(`home.footer.${item.labelKey}`);
                if (!item.external) {
                  return (
                    <Link
                      key={item.labelKey}
                      to={item.href}
                      className={styles.landingFooterNavLink}
                      onClick={
                        item.labelKey === "forBusiness"
                          ? () =>
                              trackForBusinessLinkClicked({
                                link_location: "landing_footer",
                                destination: item.href,
                              })
                          : undefined
                      }
                    >
                      {label}
                    </Link>
                  );
                }
                const isMail = item.href.startsWith("mailto:");
                return (
                  <a
                    key={item.labelKey}
                    href={item.href}
                    className={styles.landingFooterNavLink}
                    {...(!isMail
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {label}
                  </a>
                );
              })}
            </nav>
          </div>
          <div className={styles.landingFooterApps}>
            <p className={styles.landingFooterAppsTitle}>
              {t("home.footer.getApp")}
            </p>
            <div className={styles.landingFooterBadges}>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.landingFooterBadgeLink}
              >
                <img
                  src={s.appStoreBadge}
                  alt={t("home.footer.appStoreAlt")}
                  width={120}
                  height={40}
                  className={styles.landingFooterBadgeImg}
                />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.landingFooterBadgeLink}
              >
                <img
                  src={s.googlePlayBadge}
                  alt={t("home.footer.playStoreAlt")}
                  width={135}
                  height={40}
                  className={styles.landingFooterBadgeImg}
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.landingFooterInner}>
        <div className={styles.landingFooterLegal}>
          <p>{t("home.footer.riskDisclaimer")}</p>
          <p>{t("home.footer.accuracyDisclaimer")}</p>
          <p>{t("home.footer.termsCaps")}</p>
          <p>
            {t("home.footer.trademarks")}
            <br />
            <br />
            {t("home.footer.companyLine")}
            <br />
            <br />
          </p>
          <p>
            <Trans
              i18nKey="home.footer.visitDisclosures"
              components={{
                disclosuresLink: (
                  <Link
                    to={Paths.DISCLOSURES}
                    className={styles.landingFooterLegalLink}
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>

      <div className={styles.landingFooterInner}>
        <div className={styles.landingFooterBottom}>
          <p className={styles.landingFooterCopyright}>
            {t("home.footer.copyright", { year })}
          </p>
          <div className={styles.landingFooterSocials}>
            {SOCIAL_LINKS.map(({ href, icon, labelKey }) => {
              const label = t(`home.footer.${labelKey}`);
              const isTelegram = labelKey === "socialTelegram";
              return (
                <a
                  key={labelKey}
                  href={href}
                  className={styles.landingFooterSocialLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <img
                    src={icon}
                    alt=""
                    width={isTelegram ? 28 : 24}
                    height={isTelegram ? 28 : 24}
                  />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};
